import React, { useState, useEffect } from 'react';
import axios from 'axios';
import SiretLookup from './SiretLookup';
import './FichierPro.css';

const API_URL = process.env.REACT_APP_API_URL || '';

const calculateEndTime = (startTime) => {
  const [hours, minutes] = startTime.split(':').map(Number);
  const endHours = hours + 1;
  return `${endHours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
};

const statutLabels = {
  nouveau: 'Nouveau',
  a_rappeler: 'A rappeler',
  interesse: 'Intéressé',
  pas_interesse: 'Pas intéressé',
  rdv_pris: 'RDV pris',
  ne_pas_rappeler: 'Ne pas rappeler',
  nrp: 'NRP',
  hors_criteres: 'Ne rentre pas dans les critères',
  faux_num: 'Faux numéro'
};

const STATUTS_EXCLUSIFS_CEE = ['a_rappeler', 'interesse', 'pas_interesse', 'rdv_pris', 'ne_pas_rappeler', 'nrp', 'hors_criteres', 'faux_num'];

function ProspectCEE({ currentUser, pole = 'cee' }) {
  const poleLabel = pole === 'pacpv' ? 'PAC/PV' : 'CEE';
  const [secteur, setSecteur] = useState('');
  const [ville, setVille] = useState('');
  const [codePostal, setCodePostal] = useState('');
  const [searchText, setSearchText] = useState('');
  const [filterStatut, setFilterStatut] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalResults, setTotalResults] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [searchDone, setSearchDone] = useState(false);
  const [dbStats, setDbStats] = useState({ totalInDB: 0, bySecteur: [] });
  const [secteurs, setSecteurs] = useState([]);
  const [detailFiche, setDetailFiche] = useState(null);
  const [exportLoading, setExportLoading] = useState(false);
  const [commentDraft, setCommentDraft] = useState('');
  const [savingComment, setSavingComment] = useState(false);
  const [transforming, setTransforming] = useState(null);

  // RDV Modal state
  const [showModal, setShowModal] = useState(false);
  const [commerciaux, setCommerciaux] = useState([]);
  const [rdvCreated, setRdvCreated] = useState({});
  const [formData, setFormData] = useState({
    commercial_id: '',
    titre: '',
    description: '',
    client_nom: '',
    client_telephone: '',
    client_adresse: '',
    date_rdv: '',
    heure_debut: '09:00',
    heure_fin: '10:00',
    statut: 'planifie'
  });

  const ITEMS_PER_PAGE = 50;

  useEffect(() => {
    const token = localStorage.getItem('token');
    const fetchCommerciaux = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/planning/commerciaux?type=${pole}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setCommerciaux(response.data);
      } catch (err) {
        console.error('Erreur chargement commerciaux:', err);
      }
    };
    const fetchFilters = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/prospect-cee/filters?pole=${pole}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSecteurs(response.data.secteurs || []);
        setDbStats(prev => ({ ...prev, totalInDB: response.data.total || 0 }));
      } catch (err) {
        console.error('Erreur chargement filtres CEE:', err);
      }
    };
    fetchCommerciaux();
    fetchFilters();
  }, []);

  const handleStatutChange = async (id, newStatut) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/prospect-cee/statut`, {
        id,
        statut: newStatut,
        pole
      }, { headers: { Authorization: `Bearer ${token}` } });
      const isExclusif = STATUTS_EXCLUSIFS_CEE.includes(newStatut);
      setResults(prev => prev.map(r =>
        r.id === id ? {
          ...r,
          statut: newStatut,
          statut_par: isExclusif ? currentUser.id : null,
          statut_par_nom: isExclusif ? currentUser.nom : null,
          statut_date: isExclusif ? new Date().toISOString() : null
        } : r
      ));
    } catch (err) {
      console.error('Erreur changement statut:', err);
      if (err.response && err.response.status === 403) {
        alert(err.response.data.error || 'Cette fiche est assignée à un autre utilisateur');
      }
    }
  };

  const openDetail = (prospect) => {
    setDetailFiche(prospect);
    setCommentDraft(prospect.commentaire || '');
  };

  const handleSaveComment = async () => {
    if (!detailFiche) return;
    setSavingComment(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/prospect-cee/commentaire`, {
        id: detailFiche.id, commentaire: commentDraft, pole
      }, { headers: { Authorization: `Bearer ${token}` } });
      setResults(prev => prev.map(r => r.id === detailFiche.id ? { ...r, commentaire: commentDraft } : r));
      setDetailFiche(prev => ({ ...prev, commentaire: commentDraft }));
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur lors de l\'enregistrement du commentaire');
    } finally {
      setSavingComment(false);
    }
  };

  const handleTransform = async (prospect) => {
    if (prospect.lead_id) { alert(`Ce prospect a déjà été transformé en lead ${poleLabel}.`); return; }
    if (!window.confirm(`Transformer « ${prospect.nom} » en lead ${poleLabel} ?${pole === 'cee' ? '\nIl sera attribué automatiquement à Maréchal ou Bouquet.' : ''}`)) return;
    setTransforming(prospect.id);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API_URL}/api/prospect-cee/${prospect.id}/to-lead`, { pole }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const leadId = res.data.lead_id;
      setResults(prev => prev.map(r => r.id === prospect.id ? { ...r, lead_id: leadId } : r));
      setDetailFiche(prev => (prev && prev.id === prospect.id) ? { ...prev, lead_id: leadId } : prev);
      alert(`« ${prospect.nom} » transformé en lead ${poleLabel} ✓ (attribué à ${res.data.lead?.agent_nom || 'un traiteur'})`);
    } catch (err) {
      if (err.response?.status === 409 && err.response.data?.lead_id) {
        setResults(prev => prev.map(r => r.id === prospect.id ? { ...r, lead_id: err.response.data.lead_id } : r));
      }
      alert(err.response?.data?.error || 'Erreur lors de la transformation en lead');
    } finally {
      setTransforming(null);
    }
  };

  const handleExportCSV = async () => {
    setExportLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (secteur) params.append('secteur', secteur);
      if (ville) params.append('ville', ville);
      if (codePostal) params.append('code_postal', codePostal);
      if (searchText) params.append('search', searchText);
      if (filterStatut) params.append('statut', filterStatut);

      const response = await axios.get(`${API_URL}/api/prospect-cee/export-csv?${params.toString()}&pole=${pole}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
        timeout: 120000
      });

      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const today = new Date().toISOString().slice(0, 10);
      link.setAttribute('download', `prospects-cee-${today}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur lors de l\'export CSV');
    } finally {
      setExportLoading(false);
    }
  };

  const handleSearchBase = async (pageNum) => {
    const targetPage = typeof pageNum === 'number' ? pageNum : 1;
    setLoading(true);
    setError('');
    if (targetPage === 1) setResults([]);
    setSearchDone(false);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/api/prospect-cee/base`, {
        secteur: secteur || undefined,
        ville: ville || undefined,
        code_postal: codePostal || undefined,
        search: searchText || undefined,
        statut: filterStatut || undefined,
        pole,
        page: targetPage,
        limit: ITEMS_PER_PAGE
      }, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 15000
      });

      setResults(response.data.results || []);
      setTotalResults(response.data.total || 0);
      setCurrentPage(targetPage);
      setTotalPages(response.data.totalPages || 1);
      setDbStats(response.data.stats || { totalInDB: 0, bySecteur: [] });
      setSearchDone(true);
    } catch (err) {
      const msg = err.response?.data?.error || 'Erreur lors de la consultation de la base.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenRdvModal = (result) => {
    setFormData({
      commercial_id: commerciaux[0]?.id || '',
      titre: `RDV ${poleLabel} - ${result.nom}`,
      description: `Source: Prospect ${poleLabel}${result.secteur ? `\nSecteur: ${result.secteur}` : ''}${result.siret ? `\nSIRET: ${result.siret}` : ''}${result.dirigeant ? `\nDirigeant: ${result.dirigeant}` : ''}${result.site_web ? `\nSite web: ${result.site_web}` : ''}`,
      client_nom: result.nom,
      client_telephone: result.telephone,
      client_adresse: [result.adresse, result.code_postal, result.ville].filter(Boolean).join(' '),
      date_rdv: new Date().toISOString().split('T')[0],
      heure_debut: '09:00',
      heure_fin: '10:00',
      statut: 'planifie'
    });
    setShowModal(true);
  };

  const handleSubmitRdv = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const dataToSend = {
        ...formData,
        heure_fin: calculateEndTime(formData.heure_debut),
        type: pole
      };
      await axios.post(`${API_URL}/api/planning/rdv`, dataToSend, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShowModal(false);
      const currentResult = results.find(r =>
        r.nom === formData.client_nom && r.telephone === formData.client_telephone
      );
      if (currentResult) {
        setRdvCreated(prev => ({ ...prev, [currentResult.id]: true }));
      }
    } catch (err) {
      console.error('Erreur creation RDV:', err);
      alert(err.response?.data?.error || 'Erreur lors de la creation du RDV');
    }
  };

  return (
    <div className="fichier-pro">
      <div className="fichier-pro-header">
        <h2>Prospect {poleLabel}</h2>
        <p className="fichier-pro-subtitle">
          Base de prospection {poleLabel} | <strong>{dbStats.totalInDB}</strong> fiches enregistrées
        </p>
      </div>

      {/* Filtres */}
      <div className="fichier-pro-filters">
        <div className="filter-group">
          <label>Secteur</label>
          <select value={secteur} onChange={e => setSecteur(e.target.value)}>
            <option value="">-- Tous --</option>
            {secteurs.map(s => (
              <option key={s.secteur} value={s.secteur}>{s.secteur} ({s.count})</option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label>Ville</label>
          <input
            type="text"
            className="filter-input"
            value={ville}
            onChange={e => setVille(e.target.value)}
            placeholder="Ville..."
            onKeyDown={e => e.key === 'Enter' && handleSearchBase()}
          />
        </div>

        <div className="filter-group">
          <label>Code postal / Dépt</label>
          <input
            type="text"
            className="filter-input"
            value={codePostal}
            onChange={e => setCodePostal(e.target.value)}
            placeholder="Ex: 75 ou 75011"
            onKeyDown={e => e.key === 'Enter' && handleSearchBase()}
          />
        </div>

        <div className="filter-group">
          <label>Recherche libre</label>
          <input
            type="text"
            className="filter-input"
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
            placeholder="Nom, adresse, tél, dirigeant..."
            onKeyDown={e => e.key === 'Enter' && handleSearchBase()}
          />
        </div>

        <div className="filter-group">
          <label>Statut</label>
          <select value={filterStatut} onChange={e => setFilterStatut(e.target.value)}>
            <option value="">-- Tous --</option>
            {Object.entries(statutLabels).map(([key, label]) => (
              <option key={key} value={key}>{label}</option>
            ))}
          </select>
        </div>

        <button
          className="btn-search"
          onClick={() => handleSearchBase()}
          disabled={loading}
        >
          {loading ? (
            <><span className="spinner-small"></span> Recherche...</>
          ) : (
            'Consulter'
          )}
        </button>

        {currentUser?.role === 'admin' && (
          <button
            className="btn-search"
            style={{ background: '#2e7d32', marginLeft: '8px' }}
            onClick={handleExportCSV}
            disabled={exportLoading}
            title="Exporter en CSV (avec les filtres actifs)"
          >
            {exportLoading ? (
              <><span className="spinner-small"></span> Export...</>
            ) : (
              'Exporter CSV'
            )}
          </button>
        )}
      </div>

      {/* Erreur */}
      {error && (
        <div className="fichier-pro-error">
          {error}
        </div>
      )}

      {/* Résultats */}
      {searchDone && !loading && (
        <div className="fichier-pro-results">
          <div className="results-header">
            <span className="results-count">
              {totalResults} résultat{totalResults !== 1 ? 's' : ''} trouvé{totalResults !== 1 ? 's' : ''}
            </span>
            <div className="results-meta">
              {totalPages > 1 && (
                <span className="results-page">
                  Page {currentPage} / {totalPages}
                </span>
              )}
            </div>
          </div>

          {results.length > 0 ? (
            <>
              <div className="table-wrapper">
                <table className="fichier-pro-table">
                  <thead>
                    <tr>
                      <th>Nom</th>
                      <th>Secteur</th>
                      <th>Ville</th>
                      <th>Téléphone</th>
                      <th>Statut</th>
                      <th>Statué par</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {results.map((r, idx) => (
                      <tr key={r.id || idx}>
                        <td className="td-nom">{r.nom}</td>
                        <td>{r.secteur || '-'}</td>
                        <td>{r.ville || '-'}{r.code_postal ? ` (${r.code_postal})` : ''}</td>
                        <td className="td-phone">
                          {r.telephone ? (
                            <a href={`tel:${r.telephone}`} className="phone-link">{r.telephone}</a>
                          ) : '-'}
                        </td>
                        <td className="td-statut">
                          <select
                            className={`statut-select statut-${r.statut || 'nouveau'}`}
                            value={r.statut || 'nouveau'}
                            onChange={e => handleStatutChange(r.id, e.target.value)}
                          >
                            {Object.entries(statutLabels).map(([key, label]) => (
                              <option key={key} value={key}>{label}</option>
                            ))}
                          </select>
                        </td>
                        <td className="td-statut-par">
                          {r.statut_par_nom && (
                            <span className="statut-par-info">
                              {r.statut_par_nom}
                              <br/>
                              <small>{new Date(r.statut_date).toLocaleDateString('fr-FR')} {new Date(r.statut_date).toLocaleTimeString('fr-FR', {hour:'2-digit', minute:'2-digit'})}</small>
                            </span>
                          )}
                        </td>
                        <td className="td-actions">
                          <button className="btn-voir-fiche" onClick={() => openDetail(r)}>
                            Voir{r.commentaire ? ' 💬' : ''}
                          </button>
                          {r.lead_id ? (
                            <span className="badge-added" style={{ background: '#7b2ff7' }}>Lead ✓</span>
                          ) : (
                            <button className="btn-add-rdv" style={{ background: '#7b2ff7' }}
                              disabled={transforming === r.id}
                              title={`Transformer en lead ${poleLabel}`}
                              onClick={() => handleTransform(r)}>
                              {transforming === r.id ? '...' : '→ Lead'}
                            </button>
                          )}
                          {rdvCreated[r.id] ? (
                            <span className="badge-added">RDV</span>
                          ) : (
                            <button className="btn-add-rdv" onClick={() => handleOpenRdvModal(r)}>RDV</button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Modal détail fiche */}
              {detailFiche && (
                <div className="modal-overlay">
                  <div className="modal-content planning-modal" onClick={e => e.stopPropagation()}>
                    <div className="modal-header">
                      <h3>{detailFiche.nom}</h3>
                      <button className="btn-close" onClick={() => setDetailFiche(null)}>&times;</button>
                    </div>
                    <div className="fiche-detail-body">
                      <div className="fiche-detail-row">
                        <span className="fiche-label">Secteur</span>
                        <span className="fiche-value">{detailFiche.secteur || '-'}</span>
                      </div>
                      <div className="fiche-detail-row">
                        <span className="fiche-label">Téléphone</span>
                        <span className="fiche-value">
                          {detailFiche.telephone ? (
                            <a href={`tel:${detailFiche.telephone}`} className="phone-link">{detailFiche.telephone}</a>
                          ) : '-'}
                        </span>
                      </div>
                      <div className="fiche-detail-row">
                        <span className="fiche-label">Email</span>
                        <span className="fiche-value">
                          {detailFiche.email ? <a href={`mailto:${detailFiche.email}`} className="web-link">{detailFiche.email}</a> : '-'}
                        </span>
                      </div>
                      <div className="fiche-detail-row">
                        <span className="fiche-label">Adresse</span>
                        <span className="fiche-value">{detailFiche.adresse || '-'}</span>
                      </div>
                      <div className="fiche-detail-row">
                        <span className="fiche-label">Ville</span>
                        <span className="fiche-value">{detailFiche.ville || '-'}</span>
                      </div>
                      <div className="fiche-detail-row">
                        <span className="fiche-label">Code postal</span>
                        <span className="fiche-value">{detailFiche.code_postal || '-'}</span>
                      </div>
                      <div className="fiche-detail-row">
                        <span className="fiche-label">SIRET</span>
                        <span className="fiche-value">{detailFiche.siret || '-'}</span>
                      </div>
                      <div className="fiche-detail-row">
                        <span className="fiche-label">Dirigeant</span>
                        <span className="fiche-value">{detailFiche.dirigeant || '-'}{detailFiche.qualite_dirigeant ? ` (${detailFiche.qualite_dirigeant})` : ''}</span>
                      </div>
                      <div className="fiche-detail-row">
                        <span className="fiche-label">Site web</span>
                        <span className="fiche-value">
                          {detailFiche.site_web ? (
                            <a href={detailFiche.site_web.startsWith('http') ? detailFiche.site_web : `https://${detailFiche.site_web}`} target="_blank" rel="noopener noreferrer" className="web-link">{detailFiche.site_web}</a>
                          ) : '-'}
                        </span>
                      </div>
                      <div className="fiche-detail-row">
                        <span className="fiche-label">Statut</span>
                        <span className="fiche-value">
                          <select
                            className={`statut-select statut-${detailFiche.statut || 'nouveau'}`}
                            value={detailFiche.statut || 'nouveau'}
                            onChange={e => {
                              handleStatutChange(detailFiche.id, e.target.value);
                              setDetailFiche(prev => ({ ...prev, statut: e.target.value }));
                            }}
                          >
                            {Object.entries(statutLabels).map(([key, label]) => (
                              <option key={key} value={key}>{label}</option>
                            ))}
                          </select>
                        </span>
                      </div>
                      {detailFiche.statut_par_nom && (
                        <div className="fiche-detail-row">
                          <span className="fiche-label">Statué par</span>
                          <span className="fiche-value">
                            {detailFiche.statut_par_nom} - {new Date(detailFiche.statut_date).toLocaleDateString('fr-FR')} {new Date(detailFiche.statut_date).toLocaleTimeString('fr-FR', {hour:'2-digit', minute:'2-digit'})}
                          </span>
                        </div>
                      )}
                      <div className="fiche-detail-row" style={{ flexDirection: 'column', alignItems: 'stretch' }}>
                        <span className="fiche-label" style={{ marginBottom: '6px' }}>Commentaire</span>
                        <textarea
                          value={commentDraft}
                          onChange={e => setCommentDraft(e.target.value)}
                          placeholder="Ajouter un commentaire sur ce prospect..."
                          style={{ width: '100%', minHeight: '90px', padding: '10px', borderRadius: '8px', border: '1px solid #ccc', fontFamily: 'inherit', fontSize: '14px', resize: 'vertical', boxSizing: 'border-box' }}
                        />
                        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '8px' }}>
                          <button className="btn-search" onClick={handleSaveComment} disabled={savingComment || commentDraft === (detailFiche.commentaire || '')}>
                            {savingComment ? <><span className="spinner-small"></span> Enregistrement...</> : 'Enregistrer le commentaire'}
                          </button>
                        </div>
                      </div>
                      <div className="fiche-detail-actions">
                        {detailFiche.lead_id ? (
                          <span className="badge-added" style={{ background: '#7b2ff7' }}>Déjà transformé en lead {poleLabel} ✓</span>
                        ) : (
                          <button className="btn-add-rdv" style={{ background: '#7b2ff7' }}
                            disabled={transforming === detailFiche.id}
                            onClick={() => handleTransform(detailFiche)}>
                            {transforming === detailFiche.id ? 'Transformation...' : `→ Transformer en lead ${poleLabel}`}
                          </button>
                        )}
                        {!rdvCreated[detailFiche.id] && (
                          <button className="btn-add-rdv" onClick={() => { handleOpenRdvModal(detailFiche); setDetailFiche(null); }}>
                            Prendre RDV
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="pagination">
                  <button
                    onClick={() => handleSearchBase(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                  >
                    Précédent
                  </button>
                  {Array.from({ length: Math.min(totalPages, 10) }, (_, i) => {
                    let page;
                    if (totalPages <= 10) {
                      page = i + 1;
                    } else if (currentPage <= 5) {
                      page = i + 1;
                    } else if (currentPage >= totalPages - 4) {
                      page = totalPages - 9 + i;
                    } else {
                      page = currentPage - 4 + i;
                    }
                    return (
                      <button
                        key={page}
                        className={currentPage === page ? 'active' : ''}
                        onClick={() => handleSearchBase(page)}
                      >
                        {page}
                      </button>
                    );
                  })}
                  <button
                    onClick={() => handleSearchBase(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                  >
                    Suivant
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="no-results">
              Aucun prospect trouvé avec ces filtres.
            </div>
          )}
        </div>
      )}

      {/* Stats par secteur */}
      {searchDone && dbStats.bySecteur && dbStats.bySecteur.length > 0 && (
        <div className="fichier-pro-db-stats">
          <div className="db-stats-section">
            <h4>Par secteur</h4>
            <div className="db-stats-tags">
              {dbStats.bySecteur.map(s => (
                <span key={s.secteur} className="db-stat-tag" onClick={() => { setSecteur(s.secteur); }}>
                  {s.secteur} ({s.count})
                </span>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Modal Nouveau RDV */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content planning-modal">
            <div className="modal-header">
              <h3>Nouveau Rendez-vous</h3>
              <button className="btn-close" onClick={() => setShowModal(false)}>&times;</button>
            </div>
            <form onSubmit={handleSubmitRdv}>
              <SiretLookup
                compact
                onCompanyFound={(data) => {
                  setFormData(prev => ({
                    ...prev,
                    client_nom: data.dirigeant_nom || prev.client_nom,
                    client_adresse: data.adresse || prev.client_adresse,
                    titre: prev.titre || `RDV ${data.entreprise}`,
                    description: prev.description || `Entreprise: ${data.entreprise}${data.dirigeant_qualite ? `\nDirigeant: ${data.dirigeant_nom} (${data.dirigeant_qualite})` : ''}`
                  }));
                }}
              />
              <div className="form-row">
                <div className="form-group">
                  <label>Commercial *</label>
                  <select
                    value={formData.commercial_id}
                    onChange={e => setFormData({...formData, commercial_id: parseInt(e.target.value)})}
                    required
                  >
                    {commerciaux.map(c => (
                      <option key={c.id} value={c.id}>{c.nom}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Statut</label>
                  <select
                    value={formData.statut}
                    onChange={e => setFormData({...formData, statut: e.target.value})}
                  >
                    <option value="planifie">Planifié</option>
                    {(currentUser?.role === 'admin' || currentUser?.id === 2) && (
                      <option value="confirme">Confirmé</option>
                    )}
                    <option value="annule">Annulé</option>
                    <option value="termine">Terminé</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label>Titre du RDV *</label>
                <input
                  type="text"
                  value={formData.titre}
                  onChange={e => setFormData({...formData, titre: e.target.value})}
                  placeholder="Ex: Presentation produit, Signature contrat..."
                  required
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Date *</label>
                  <input
                    type="date"
                    value={formData.date_rdv}
                    onChange={e => setFormData({...formData, date_rdv: e.target.value})}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Heure *</label>
                  <input
                    type="time"
                    value={formData.heure_debut}
                    onChange={e => setFormData({...formData, heure_debut: e.target.value})}
                    required
                  />
                  <small className="form-hint">Durée: 1h</small>
                </div>
              </div>

              <div className="form-group">
                <label>Nom du client</label>
                <input
                  type="text"
                  value={formData.client_nom}
                  onChange={e => setFormData({...formData, client_nom: e.target.value})}
                  placeholder="Nom du client ou prospect"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Téléphone client</label>
                  <input
                    type="tel"
                    value={formData.client_telephone}
                    onChange={e => setFormData({...formData, client_telephone: e.target.value})}
                    placeholder="06 XX XX XX XX"
                  />
                </div>
                <div className="form-group">
                  <label>Adresse</label>
                  <input
                    type="text"
                    value={formData.client_adresse}
                    onChange={e => setFormData({...formData, client_adresse: e.target.value})}
                    placeholder="Lieu du rendez-vous"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Description / Notes</label>
                <textarea
                  value={formData.description}
                  onChange={e => setFormData({...formData, description: e.target.value})}
                  rows="3"
                  placeholder="Détails du rendez-vous..."
                />
              </div>

              <div className="modal-actions">
                <button type="button" className="btn-cancel" onClick={() => setShowModal(false)}>
                  Annuler
                </button>
                <button type="submit" className="btn-submit">
                  Créer le RDV
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default ProspectCEE;
